import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { GraphQL } from "@rpi/openapi-api";
import { RpiDrawerSelectInput, RpiDrawerTextInput } from "@rpi/openapi-core";
import React from "react";
import { useAuthUser } from "../../../hooks/useAuthUser";
import {
  createDrawerOverlay,
  createRpiDrawerView,
  RpiDrawer,
  RpiDrawerForm,
  RpiDrawerProps,
} from "../../../rpi-core/drawer/RpiDrawer";
import { validateEmail } from "../../../utils/validation.util";
import { dashboardUserRoleLabel } from "../../../utils/graphql.util";

export const DashboardUserCreateView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    return (
      <RpiDrawer title="Add new user" opened={opened} onClose={handleClose}>
        <CreateUser onClose={() => handleClose(true)} />
      </RpiDrawer>
    );
  }
);

interface CreateUserProps {
  onClose: () => void;
}

const CreateUser: React.FC<CreateUserProps> = ({ onClose }) => {
  const [customersSearch, setCustomersSearch] = React.useState("");
  const [debouncedCustomersSearch] = useDebouncedValue(customersSearch, 250);

  const user = useAuthUser();

  const { data, ...customersQuery } = GraphQL.useAdminListCustomersQuery(
    { input: debouncedCustomersSearch },
    { enabled: user.is.SUPER_ADMIN }
  );
  const customers = React.useMemo(() => data?.adminListCustomers, [data]);

  const {
    mutateAsync: createDashboardUser,
    reset,
    ...mutation
  } = GraphQL.useCreateDashboardUserMutation<{ message: string }>();

  const form = useForm<GraphQL.CreateDashboardUserMutationVariables["input"]>({
    initialValues: {
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      customerPublicId: !user.is.SUPER_ADMIN ? user.id : "",
      role: GraphQL.DashboardUserRole.User,
    },
    validate: {
      username: (value: string) =>
        value?.length > 1 && value.length <= 128 ? null : "Username must be between 1 and 128 characters",
      firstName: (value: string) => (value.length <= 64 ? null : "First name must lower than 64 characters"),
      lastName: (value: string) => (value.length <= 64 ? null : "Last name must lower than 64 characters"),
      email: validateEmail,
    },
  });

  React.useEffect(() => reset, []);

  const handleSubmit = React.useCallback(
    async (input: typeof form.values) => {
      await createDashboardUser({ input });
      onClose();
    },
    [createDashboardUser, onClose]
  );

  const overlay = React.useMemo(
    () =>
      createDrawerOverlay({
        loading: {
          message: "Creating user...",
          description: "Please wait, do not close this screen.",
        },
        error: {
          message: mutation.error?.message || "Failed to create user...",
          description: "Please try again later.",
        },
        success: {
          message: "User created!",
        },
      })(mutation),
    [mutation]
  );

  const customersSelectData = React.useMemo(
    () => (customers || []).map((customer) => ({ label: customer.publicId, value: customer.publicId })),
    [customers]
  );

  const roleSelectData = React.useMemo(() => {
    return [
      GraphQL.DashboardUserRole.User,
      GraphQL.DashboardUserRole.CustomerAdmin,
      ...(user.is.SUPER_ADMIN ? [GraphQL.DashboardUserRole.SuperAdmin] : []),
    ].map((value) => ({ label: dashboardUserRoleLabel(value), value }));
  }, [user]);

  return (
    <RpiDrawerForm button="Save" overlay={overlay} onSubmit={form.onSubmit(handleSubmit)}>
      {user.is.SUPER_ADMIN && (
        <RpiDrawerSelectInput
          required
          label="Customer"
          data={customersSelectData}
          {...form.getInputProps("customerPublicId")}
          searchable
          onSearch={setCustomersSearch}
          isLoading={customersQuery.isLoading}
        />
      )}

      <RpiDrawerTextInput required label="Username" {...form.getInputProps("username")} />
      <RpiDrawerTextInput label="First Name" {...form.getInputProps("firstName")} />
      <RpiDrawerTextInput label="Last Name" {...form.getInputProps("lastName")} />
      <RpiDrawerTextInput required label="Email" {...form.getInputProps("email")} />

      <RpiDrawerSelectInput required label="Role" data={roleSelectData} {...form.getInputProps("role")} />
    </RpiDrawerForm>
  );
};
